import { loadingReducers, LoadingState } from './base/baseModelStore';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RainfallType, RainHistoryRow, RulesConfigurationDetails } from '@api-model/models';
import { AppThunk } from '@root/store/store';
import { httpClient } from '@infrastructure/http/HttpClient';
import { RainfallEnabledDetails, RainUnits } from '@api-model/rainfallModel';

export type RainfallHistogramBar = { [key: string]: number };

export interface RainfallState extends LoadingState {
  rainfallEnabled: boolean;
  rainfallUnits: RainUnits;
  siteRules: RulesConfigurationDetails;
  accountRules: RulesConfigurationDetails;
  rainHistoryTable: RainHistoryRow[];
  histogram: RainfallHistogramBar;
}

const loadingRainfallEnabledSuccess = (state: RainfallState, { payload }: PayloadAction<RainfallEnabledDetails>) => {
  state.rainfallEnabled = payload.isEnabled;
  state.rainfallUnits = payload.units ?? RainUnits.MilliMeters;
  state.loading = false;
};

const loadingAccRainfallRulesSuccess = (
  state: RainfallState,
  { payload }: PayloadAction<RulesConfigurationDetails>
) => {
  state.accountRules = payload;
  state.loading = false;
};

const loadingSitesRainfallRulesSuccess = (
  state: RainfallState,
  { payload }: PayloadAction<RulesConfigurationDetails>
) => {
  state.siteRules = payload;
  state.loading = false;
};

const loadingRainHistoryTableSuccess = (state: RainfallState, { payload }: PayloadAction<RainHistoryRow[]>) => {
  state.rainHistoryTable = payload;
  state.loading = false;
};

const loadingHistogramSuccess = (state: RainfallState, { payload }: PayloadAction<RainfallHistogramBar>) => {
  state.histogram = payload;
  state.loading = false;
};

const slice = createSlice({
  name: 'rainfallRules',
  initialState: {
    loading: false,
    error: null,
    rainfallEnabled: false,
    rainfallUnits: RainUnits.MilliMeters,
    siteRules: {} as RulesConfigurationDetails,
    accountRules: {} as RulesConfigurationDetails,
    rainHistoryTable: [] as RainHistoryRow[],
    histogram: {} as RainfallHistogramBar,
  } as RainfallState,
  reducers: {
    ...loadingReducers,
    loadingRainfallEnabledSuccess,
    loadingAccRainfallRulesSuccess,
    loadingSitesRainfallRulesSuccess,
    loadingRainHistoryTableSuccess,
    loadingHistogramSuccess,
  },
});

const actions = slice.actions;

const getRainfallEnabled = (): AppThunk => async (dispatch) => {
  try {
    dispatch(actions.startLoading());
    const { data } = await httpClient.get<RainfallEnabledDetails>('Rainfall/settings');
    dispatch(actions.loadingRainfallEnabledSuccess(data));
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const getRainfallRuleConfig = (type: RainfallType, id: number, units: RainUnits): AppThunk => async (dispatch) => {
  try {
    dispatch(actions.startLoading());
    const url = (type === 'Site' ? 'Chambers' : 'accounts') + `/${id}/configuration?units=${units}`;

    const { data } = await httpClient.get<RulesConfigurationDetails>(url);

    // Add the units here to make working with forms easier
    if (data) {
      data.units = units;
    }

    type === 'Site'
      ? dispatch(actions.loadingSitesRainfallRulesSuccess(data ?? ({} as RulesConfigurationDetails)))
      : dispatch(actions.loadingAccRainfallRulesSuccess(data ?? ({} as RulesConfigurationDetails)));
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const addRainfallRuleConfig = (
  type: RainfallType,
  id: number,
  rulesData: RulesConfigurationDetails
): AppThunk => async (dispatch) => {
  try {
    dispatch(actions.startLoading());
    const url = (type === 'Site' ? 'Chambers' : 'accounts') + `/${id}/configuration`;
    const { status } = await httpClient.put(url, rulesData);
    if (status === 200) {
      type === 'Site'
        ? dispatch(actions.loadingSitesRainfallRulesSuccess(rulesData))
        : dispatch(actions.loadingAccRainfallRulesSuccess(rulesData));
    }
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const getRainHistoryTable = (siteId: number, units: RainUnits, limit?: number, start?: number): AppThunk => async (
  dispatch
) => {
  try {
    dispatch(actions.startLoading());
    const url = `Rainfall/site-history-table/${siteId}?units=${units}&limit=${limit ?? 25}&start=${start ?? 0}`;
    const { data } = await httpClient.get<RainHistoryRow[]>(url);
    dispatch(actions.loadingRainHistoryTableSuccess(data));
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const getRainfallHistogram = (siteId: number, units: RainUnits): AppThunk => async (dispatch) => {
  try {
    dispatch(actions.startLoading());
    const url = `Rainfall/site-histogram/${siteId}?units=${units}`;
    const { data } = await httpClient.get<RainfallHistogramBar>(url);
    dispatch(actions.loadingHistogramSuccess(data));
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const exportRainfallHistoryToCsv = <T>(
  siteId: number,
  units: RainUnits,
  resultCallback: (data: T) => void
): AppThunk => async (dispatch) => {
  try {
    const { data } = await httpClient.get(`Rainfall/site-history-table/${siteId}/export?units=${units}`);
    resultCallback(data);
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const thunks = {
  getRainfallEnabled,
  getRainfallRuleConfig,
  addRainfallRuleConfig,
  getRainHistoryTable,
  getRainfallHistogram,
  exportRainfallHistoryToCsv,
};

export const rainfallEntity = {
  reducer: slice.reducer,
  actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof actions;
  thunks: typeof thunks;
};
